import { ReactNode, useContext, useEffect, useLayoutEffect } from "react";
import { Navigate, Route, Routes as RouterRoutes, useLocation } from "react-router";

import { General } from "capitalrx-components-2.0";

import { AsyncWrapper } from "./AsyncWrapper";
import { ContentLayout } from "./ContentLayout";
import { Error404Page } from "./Error404Page";
import { activateModuleMetadata } from "./api";
import { BodyModuleClass, ModuleErrorBoundary } from "./components";
import { HostModuleContext, OwnerModuleContext, ShellContext } from "./context";
import { useIsFullscreenRoute, useModulePathResolver, useModules, useRootModule, useRouteCache, useShellModulePathResolver } from "./hooks";
import { useAlert } from "capitalrx-components-2.0/lib/general";

/**
 * Wrapper component that activates module metadata for HotKeys permission system
 * when a module route is entered.
 */
const ModuleMetadataActivator = ({ moduleId, children }: { moduleId: string; children: ReactNode }): ReactNode => {
  useEffect(() => {
    // Activate module metadata when entering this module's routes
    activateModuleMetadata(moduleId);

    return () => {
      // Clear metadata when leaving module (optional - next module will set its own)
      General.clearModuleMetadata();
    };
  }, [moduleId]);

  return <>{children}</>;
};

/** A component which renders out the set of content routes. */
const ContentRoutes = (): ReactNode => {
  const modules = useModules();
  const resolvePath = useModulePathResolver();
  const path404 = resolvePath("/404");
  let path = useLocation()?.pathname;
  if (path.endsWith("/")) path = path.substr(0, path.length - 1);

  return (
    <RouterRoutes>
      {modules.map((module) => {
        const { info } = module;
        const { id } = info;

        if (!module.index) return null;

        const containerProvider = async () => {
          switch (module.version) {
            case 1:
            case undefined:
              return module.container;
            case 2:
              return await module.container?.();
            default:
              return undefined;
          }
        };

        const indexProvider = async () => {
          switch (module.version) {
            case 1:
            case undefined:
              return module.index;
            case 2:
              return await module.index?.();
            default:
              return undefined;
          }
        };

        return (
          <Route
            key={id}
            path={`${id}/*`}
            element={
              <ModuleErrorBoundary>
                <ModuleMetadataActivator moduleId={id}>
                  <OwnerModuleContext.Provider value={info}>
                    <HostModuleContext.Provider value={info}>
                      <BodyModuleClass />
                      <AsyncWrapper resolver={containerProvider}>
                        <AsyncWrapper resolver={indexProvider} />
                      </AsyncWrapper>
                    </HostModuleContext.Provider>
                  </OwnerModuleContext.Provider>
                </ModuleMetadataActivator>
              </ModuleErrorBoundary>
            }
          />
        );
      })}
      <Route element={<Navigate to={path404} />} />
    </RouterRoutes>
  );
};

/** The `Routes` component properties. */
export interface RoutesProps {
  /** The fallback should no routes match. */
  readonly children?: () => ReactNode;
}

/** The routes component. */
export const Routes = (props: RoutesProps): ReactNode => {
  const { children } = props;
  const location = useLocation();

  const showAlert = useAlert();

  const { moduleRootPath } = useContext(ShellContext);

  const resolvePath = useShellModulePathResolver();
  const rootModule = useRootModule();
  const modules = useModules();

  // Create memoized route cache for O(1) lookups
  const routeCache = useRouteCache(modules);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const alertInfo = location?.state?.alert;
    if (alertInfo?.message) {
      showAlert(alertInfo);
    }
  }, [location?.state?.alert, showAlert]);

  // Use optimized fullscreen detection with route cache
  const isFullscreen = useIsFullscreenRoute(location.pathname, routeCache, moduleRootPath);

  return (
    <ContentLayout fullscreen={isFullscreen}>
      <RouterRoutes>
        {rootModule && <Route path="/" element={<Navigate to={resolvePath(rootModule, "/")} />} />}
        <Route path={`${moduleRootPath}/*`} element={<ContentRoutes />} />
        <Route path="/404" element={<Error404Page />} />
        <Route path="/*" element={children ? children() : <Error404Page />} />
      </RouterRoutes>
    </ContentLayout>
  );
};
